import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Clock, User, Calendar, ArrowRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FOUNDER_IMG =
  'https://customer-assets.emergentagent.com/job_6fe4af30-54a6-49c2-888a-b800c65f893f/artifacts/cobplphu_IMG_8450.JPG';

const InsightDetailPage = () => {
  const { slug } = useParams();
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchInsight = async () => {
      try {
        const response = await axios.get(`${API}/insights/${slug}`);
        setInsight(response.data);
      } catch (err) {
        setError('Article not found');
      } finally {
        setLoading(false);
      }
    };
    fetchInsight();
    window.scrollTo({ top: 0 });
  }, [slug]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (loading) {
    return (
      <div data-testid="insight-loading" className="pt-32 pb-32 min-h-screen flex items-center justify-center bg-[#1B0E33]">
        <p className="text-slate-400">Loading article...</p>
      </div>
    );
  }

  if (error || !insight) {
    return (
      <div data-testid="insight-error" className="pt-32 pb-32 min-h-screen flex flex-col items-center justify-center gap-6 bg-[#1B0E33]">
        <p className="text-slate-400 text-lg">{error || 'Article not found'}</p>
        <Link to="/#insights">
          <Button variant="outline" className="border-[#D4AF37]/50 text-[#E5C75B] hover:bg-[#D4AF37] hover:text-[#1B0E33]">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Insights
          </Button>
        </Link>
      </div>
    );
  }

  const formatContent = (content) =>
    content.split('\n\n').map((paragraph, index) => {
      if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
        return (
          <h3 key={index} className="font-display text-xl md:text-2xl font-semibold text-white mt-10 mb-4">
            {paragraph.replace(/\*\*/g, '')}
          </h3>
        );
      }
      if (paragraph.startsWith('- ')) {
        const items = paragraph.split('\n').filter((i) => i.startsWith('- '));
        return (
          <ul key={index} className="list-disc list-inside space-y-2 my-4 text-slate-300">
            {items.map((item, i) => (
              <li key={i}>{item.substring(2)}</li>
            ))}
          </ul>
        );
      }
      if (/^\d+\./.test(paragraph)) {
        const items = paragraph.split('\n').filter((i) => /^\d+\./.test(i));
        return (
          <ol key={index} className="list-decimal list-inside space-y-3 my-6 text-slate-300">
            {items.map((item, i) => {
              const text = item.replace(/^\d+\.\s*/, '');
              if (text.includes('**')) {
                const parts = text.split(/\*\*/);
                return (
                  <li key={i} className="leading-relaxed">
                    {parts.map((part, j) =>
                      j % 2 === 1 ? (
                        <strong key={j} className="text-[#E5C75B] font-semibold">
                          {part}
                        </strong>
                      ) : (
                        part
                      )
                    )}
                  </li>
                );
              }
              return (
                <li key={i} className="leading-relaxed">
                  {text}
                </li>
              );
            })}
          </ol>
        );
      }
      if (paragraph.includes('**')) {
        const parts = paragraph.split(/\*\*/);
        return (
          <p key={index} className="text-base md:text-lg text-slate-300 leading-relaxed my-6">
            {parts.map((part, j) =>
              j % 2 === 1 ? (
                <strong key={j} className="text-white font-semibold">
                  {part}
                </strong>
              ) : (
                part
              )
            )}
          </p>
        );
      }
      return (
        <p key={index} className="text-base md:text-lg text-slate-300 leading-relaxed my-6">
          {paragraph}
        </p>
      );
    });

  return (
    <div data-testid="insight-detail-page" className="bg-[#1B0E33] pt-20">
      {/* Header */}
      <section className="pt-16 md:pt-24 pb-12 md:pb-16 bg-[#1B0E33]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Link
            to="/#insights"
            data-testid="back-to-insights"
            className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.18em] text-[#D4AF37] hover:text-[#E5C75B] transition-colors mb-10"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Insights
          </Link>

          <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-[#D4AF37] mb-5">
            {insight.category}
          </p>

          <h1
            data-testid="insight-title"
            className="font-display text-3xl md:text-4xl lg:text-5xl font-semibold text-white leading-[1.15] mb-6"
          >
            {insight.title}
          </h1>

          <div className="vs-rule mb-6" />

          <p data-testid="insight-excerpt" className="text-lg md:text-xl text-slate-300 leading-relaxed mb-10">
            {insight.excerpt}
          </p>

          <div className="flex flex-wrap items-center gap-6 text-sm text-slate-400 border-t border-white/5 pt-6">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-[#D4AF37]" />
              <span data-testid="insight-author">{insight.author}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-[#D4AF37]" />
              <span data-testid="insight-date">{formatDate(insight.published_at)}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#D4AF37]" />
              <span data-testid="insight-read-time">{insight.read_time} min read</span>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <article data-testid="insight-content" className="py-12 md:py-16 bg-[#130723]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">{formatContent(insight.content)}</div>
      </article>

      {/* Author */}
      <section data-testid="insight-author-section" className="py-16 bg-[#1B0E33]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row gap-6 items-start border-l-2 border-[#D4AF37] pl-6 md:pl-8 py-2">
            <div className="w-20 h-20 overflow-hidden shrink-0 border border-[#D4AF37]/40">
              <img src={FOUNDER_IMG} alt={insight.author} className="w-full h-full object-cover" />
            </div>
            <div>
              <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-[#D4AF37] mb-2">Written By</p>
              <h3 className="font-display text-xl font-semibold text-white mb-2">{insight.author}</h3>
              <p className="text-slate-300 leading-relaxed">
                Physician, healthcare strategist, and founder of Vantaris Integrated Solutions. Advising healthcare systems, government agencies, and community organizations at the intersection of clinical medicine, public health, and population strategy.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="insight-cta-section" className="py-20 bg-[#130723] border-t border-white/5">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-display text-2xl md:text-3xl font-semibold text-white mb-5">
            Let's Build Healthier Systems Together.
          </h2>
          <p className="text-slate-300 mb-8 max-w-xl mx-auto">
            Schedule a consultation to discuss how Vantaris can support your organization.
          </p>
          <Link to="/#contact">
            <Button
              data-testid="insight-cta-btn"
              className="bg-[#D4AF37] text-[#1B0E33] hover:bg-[#E5C75B] px-8 py-4 h-auto rounded-sm text-[12px] font-semibold uppercase tracking-[0.12em]"
            >
              Schedule a Consultation
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default InsightDetailPage;
